import React,{createContext,useContext,useEffect,useRef,useState} from 'react';
import {Link,useLocation,useNavigate} from 'react-router-dom';
import {ExternalLink} from 'lucide-react';
import {doc,onSnapshot,getDoc} from 'firebase/firestore';
import {db} from '../../Security/firebase-config';
import {StatisticsHeaderContext} from './StatisticsHeaderActions';
import useStatisticsWorkspaceViewport from './useStatisticsWorkspaceViewport';
import './TeamStatisticsWorkspace.css';
import LibraryNavigation from '../team-library/LibraryNavigation';

const StatisticsTeamContext=createContext(null);
export const useStatisticsTeam=()=>useContext(StatisticsTeamContext);

// Both clients retain their existing records/editor and Studio implementations.
// This shared shell supplies one identity, navigation and visual language.
export default function TeamStatisticsWorkspace({teamId,teamName,teamChoices=[],teamIds=[],onTeamChange,children}) {
  const location=useLocation(),navigate=useNavigate(),studio=/\/(studio|summary)\/?$/.test(location.pathname);
  const requested=new URLSearchParams(location.search).get('teamId')||teamId;
  const workspaceRef=useRef(null);
  useStatisticsWorkspaceViewport(workspaceRef,studio);
  const [identity,setIdentity]=useState(null),[loadedChoices,setLoadedChoices]=useState([]),[headerTarget,setHeaderTarget]=useState(null);
  const idsKey=JSON.stringify(teamIds);
  useEffect(()=>{
    let active=true;
    Promise.all(JSON.parse(idsKey).map(async id=>{try{const snap=await getDoc(doc(db,'teams',id));return snap.exists()?{id,name:snap.data()?.teamName||snap.data()?.name||'Unnamed team'}:null;}catch{return null;}})).then(rows=>{if(active)setLoadedChoices(rows.filter(Boolean));});
    return()=>{active=false;};
  },[idsKey]);
  useEffect(()=>{
    if(!requested)return undefined;
    let active=true;
    const listen=(parts,set)=>onSnapshot(doc(db,'teams',requested,...parts),snapshot=>{if(active)set({id:requested,...snapshot.data()});},()=>{if(active)set(null);});
    const stops=[listen([],setIdentity)];
    return()=>{active=false;stops.forEach(stop=>stop());};
  },[requested]);
  const team=identity?.id===requested?identity:null;
  const name=team?.teamName||team?.name||teamName||'Your team';
  const choices=teamChoices.length?teamChoices:loadedChoices;
  const changeTeam=async event=>{
    const id=event.target.value;
    try{await onTeamChange?.(id);const params=new URLSearchParams(location.search);params.set('teamId',id);params.delete('match');navigate({pathname:location.pathname,search:'?'+params.toString()});}catch{/* The owning team selector reports switching failures. */}
  };
  return <StatisticsHeaderContext.Provider value={headerTarget}><section ref={workspaceRef} className="TeamStatisticsWorkspace" aria-label="Team Statistics workspace">
    <LibraryNavigation/>
    <header className="TeamStatisticsWorkspace__identity">
      <div className="TeamStatisticsWorkspace__mark" aria-hidden="true">{name.slice(0,1).toUpperCase()}</div>
      <div className="TeamStatisticsWorkspace__team"><span className="TeamStatisticsWorkspace__eyebrow">{studio?'Metrics Studio':'Team Statistics'}</span>
        <h1>{choices.length>1?<select aria-label="Statistics team" value={requested||''} onChange={changeTeam}>{!choices.some(choice=>choice.id===requested)&&<option value={requested||''}>{name}</option>}{choices.map(choice=><option key={choice.id} value={choice.id}>{choice.name}</option>)}</select>:name}</h1>
      </div>
      <div className="TeamStatisticsWorkspace__headerTools"><div ref={setHeaderTarget} className="TeamStatisticsWorkspace__headerControls"/>{window.desktopAPI&&!studio&&<Link className="TeamStatisticsWorkspace__centerLink" to={'/team-center/dashboard'+(requested?'?teamId='+encodeURIComponent(requested):'')} title="Open Team Center" aria-label="Open Team Center"><ExternalLink size={16}/></Link>}</div>
    </header>
    <StatisticsTeamContext.Provider value={team}><div className={`TeamStatisticsWorkspace__content${studio?' is-studio':' is-records'}`} aria-label={studio?'Metrics Studio':'Team Stats'}>{children}</div></StatisticsTeamContext.Provider>
  </section></StatisticsHeaderContext.Provider>;
}
